import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Luggage, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Brand } from '@/components/Brand';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';

const LINKS = [
  { to: '/portal/viagens', label: 'Minhas Viagens', icon: Luggage },
  { to: '/portal/perfil', label: 'Meu Perfil', icon: UserRound },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initials = (user?.name || 'C').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  const doLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="sticky top-0 z-30 h-16 bg-card/90 backdrop-blur border-b border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Brand />
          </Link>
          <nav className="hidden sm:flex items-center gap-1">
            {LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${location.pathname === l.to ? 'bg-primary text-primary-foreground' : 'hover:bg-accent/60'}`} data-testid={`portal-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <l.icon className="h-4 w-4" /> {l.label}
              </Link>
            ))}
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full hover:bg-accent/60 p-1 pr-3" data-testid="portal-user-menu">
                <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-xs">{initials}</AvatarFallback></Avatar>
                <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/portal/perfil')}>Meu Perfil</DropdownMenuItem>
              <DropdownMenuItem onClick={doLogout} className="text-destructive"><LogOut className="h-4 w-4 mr-2" /> Sair</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <nav className="sm:hidden border-b border-border/60 bg-card">
        <div className="flex">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm ${location.pathname === l.to ? 'text-primary font-medium border-b-2 border-primary' : 'text-muted-foreground'}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </Link>
          ))}
        </div>
      </nav>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
